import { z } from 'zod';
import {
  SchoolRole, SportType, SkillCategory, EquipmentType,
  SessionType, SessionStatus, EnrollmentType, EnrollmentStatus,
  AttendanceStatus, SubscriptionStatus, CreditStatus, PaymentType,
  PaymentStatus, AvailabilityType, Gender, AvailabilityStatus,
  ResignationRule, DiscountCouponStatus, CouponType, SkillLevelCategory,
} from './enums';

// ---- Person names ----

/**
 * Person names in Latin letters only (diacritics such as ą, ö, č allowed), so coaches can read every name.
 * Mirrors the backend `LatinNameValidation` rule and the Keycloak user profile pattern.
 */
export const LATIN_NAME_PATTERN = /^[\p{Script=Latin}\s'’.()-]*$/u;

/** i18n key of the error shown when a name contains non-Latin characters. */
export const LATIN_NAME_ERROR = 'validation.latinName';

// ---- Schools ----

export const SchoolResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  alias: z.string(),
  address: z.string().nullable().optional(),
  sportType: z.nativeEnum(SportType),
  timezone: z.string(),
  currency: z.string(),
  makeUpCreditExpiryDays: z.number(),
  allowedSessionDurations: z.string(),
  logoUrl: z.string().nullable(),
  slogan: z.string().nullable(),
  primaryColor: z.string().nullable(),
  secondaryColor: z.string().nullable(),
  isActive: z.boolean(),
  occupancyThresholds: z.string(),
  resignationRule: z.nativeEnum(ResignationRule),
  subscriptionDiscountTiers: z.string(),
  showProgress: z.boolean(),
  enabledFeatures: z.string().optional(),
  subscriptionBillingDay: z.number().optional(),
  autoIssueSubscriberDropInVoucher: z.boolean().optional(),
  instructionUrl: z.string().nullable().optional(),
  supportEmail: z.string().nullable().optional(),
  supportTopics: z.string().nullable().optional(),
});

export type SchoolResponse = z.infer<typeof SchoolResponseSchema>;

export const CreateSchoolRequestSchema = z.object({
  name: z.string().min(1, 'School name is required').max(200),
  alias: z.string().min(3, 'Alias must be at least 3 characters').max(20)
    .regex(/^[a-zA-Z0-9_\-.]+$/, 'Only letters, digits, hyphens, underscores and dots allowed'),
  address: z.string().max(500).optional(),
  sportType: z.nativeEnum(SportType),
  timezone: z.string().min(1, 'Timezone is required'),
  currency: z.string().length(3, 'Currency must be 3 characters (e.g. PLN)'),
  makeUpCreditExpiryDays: z.number().min(1).max(1080).default(21),
});

export type CreateSchoolRequest = z.infer<typeof CreateSchoolRequestSchema>;

export const ProvisionSchoolRequestSchema = z.object({
  name: z.string().min(1, 'School name is required').max(200),
  alias: z.string().min(3, 'Alias must be at least 3 characters').max(20)
    .regex(/^[a-zA-Z0-9_\-.]+$/, 'Only letters, digits, hyphens, underscores and dots allowed'),
  address: z.string().max(500).optional(),
  sportType: z.nativeEnum(SportType),
  timezone: z.string().min(1, 'Timezone is required'),
  currency: z.string().length(3, 'Currency must be 3 characters (e.g. PLN)'),
  adminEmail: z.string().email('Valid email is required'),
  adminFirstName: z.string().max(100).optional(),
  adminLastName: z.string().max(100).optional(),
  makeUpCreditExpiryDays: z.number().min(1).max(1080).default(21),
});

export type ProvisionSchoolRequest = z.infer<typeof ProvisionSchoolRequestSchema>;

export type ProvisionSchoolResponse = {
  school: SchoolResponse;
  adminUserId: string;
  adminEmail: string;
};

export const UpdateSchoolRequestSchema = z.object({
  name: z.string().min(1).max(200),
  alias: z.string().min(3).max(20)
    .regex(/^[a-zA-Z0-9_\-.]+$/, 'Only letters, digits, hyphens, underscores and dots allowed'),
  sportType: z.nativeEnum(SportType),
  timezone: z.string().min(1),
  currency: z.string().length(3),
  makeUpCreditExpiryDays: z.number().min(1).max(365),
  allowedSessionDurations: z.string().min(1),
  occupancyThresholds: z.string().min(1),
  resignationRule: z.nativeEnum(ResignationRule),
  subscriptionDiscountTiers: z.string(),
  showProgress: z.boolean(),
  subscriptionBillingDay: z.number().min(1).max(28),
  autoIssueSubscriberDropInVoucher: z.boolean().optional(),
  instructionUrl: z.string().max(2000).nullable().optional(),
  supportEmail: z.string().max(200).nullable().optional(),
  supportTopics: z.string().max(2000).nullable().optional(),
});

export type UpdateSchoolRequest = z.infer<typeof UpdateSchoolRequestSchema>;

export const SendSupportTicketRequestSchema = z.object({
  topic: z.string().min(1, 'Topic is required').max(200),
  message: z.string().min(1, 'Message is required').max(4000),
  email: z.string().email('Valid email is required').max(200),
  phoneNumber: z.string().max(50).nullable().optional(),
});

export type SendSupportTicketRequest = z.infer<typeof SendSupportTicketRequestSchema>;

export const UpdateSchoolBrandingRequestSchema = z.object({
  logoUrl: z.string().max(2000).nullable().optional(),
  slogan: z.string().max(300).nullable().optional(),
  primaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/).nullable().optional(),
  secondaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/).nullable().optional(),
});

export type UpdateSchoolBrandingRequest = z.infer<typeof UpdateSchoolBrandingRequestSchema>;

export const StudentInvoiceDetailsResponseSchema = z.object({
  schoolMemberId: z.string().uuid().optional(),
  requestVatInvoice: z.boolean(),
  companyName: z.string().nullable().optional(),
  taxId: z.string().nullable().optional(),
  street: z.string().nullable().optional(),
  postalCode: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
});

export type StudentInvoiceDetailsResponse = z.infer<typeof StudentInvoiceDetailsResponseSchema>;

export const MemberResponseSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid().nullable().optional(),
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  roles: z.number(),
  roleNames: z.array(z.string()),
  isActive: z.boolean(),
  joinedAt: z.string().datetime(),
  isPending: z.boolean(),
  skillLevelId: z.string().uuid().nullable().optional(),
  skillLevelName: z.string().nullable().optional(),
  skillCategory: z.nativeEnum(SkillCategory).nullable().optional(),
  dateOfBirth: z.string().nullable().optional(),
  bio: z.string().nullable().optional(),
  photoUrl: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  role: z.nativeEnum(SchoolRole).optional(),
  gender: z.nativeEnum(Gender),
  phoneNumber: z.string().nullable().optional(),
  receiveEmails: z.boolean().optional(),
  receivePush: z.boolean().optional(),
  healthDeclarationAccepted: z.boolean(),
  healthDeclarationAcceptedAt: z.string().datetime().nullable().optional(),
  regulationsAccepted: z.boolean(),
  regulationsAcceptedAt: z.string().datetime().nullable().optional(),
  imageConsentAccepted: z.boolean(),
  imageConsentAcceptedAt: z.string().datetime().nullable().optional(),
  lastLoginAt: z.string().datetime().nullable().optional(),
  paymentDueDay: z.number().min(1).max(28).nullable().optional(),
  parentId: z.string().uuid().nullable().optional(),
  preferredUiVersion: z.string().nullable().optional(),
  uiVersionChangedAt: z.string().datetime().nullable().optional(),
  isBetaTester: z.boolean().optional(),
  hasPushToken: z.boolean().optional(),
  invoiceDetails: StudentInvoiceDetailsResponseSchema.nullable().optional(),
  canManageTournaments: z.boolean().optional(),
  canManageRacketStringing: z.boolean().optional(),
});

export type MemberResponse = z.infer<typeof MemberResponseSchema>;

export const MemberLiteResponseSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid().nullable().optional(),
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  roles: z.number(),
  isActive: z.boolean(),
});

export type MemberLiteResponse = z.infer<typeof MemberLiteResponseSchema>;

export const MembersListResponseSchema = z.object({
  items: z.array(MemberResponseSchema),
  totalCount: z.number(),
});

export type MembersListResponse = z.infer<typeof MembersListResponseSchema>;

export const SubmitMedicalSurveyRequestSchema = z.object({
  healthDeclarationAccepted: z.boolean().refine(val => val === true, {
    message: 'Health declaration must be accepted',
  }),
  regulationsAccepted: z.boolean().refine(val => val === true, {
    message: 'Regulations must be accepted',
  }),
  imageConsentAccepted: z.boolean(),
});

export type SubmitMedicalSurveyRequest = z.infer<typeof SubmitMedicalSurveyRequestSchema>;

export const InviteMemberRequestSchema = z.object({
  userId: z.string().uuid(),
  roles: z.number().min(1),
});

export type InviteMemberRequest = z.infer<typeof InviteMemberRequestSchema>;

export const InviteUserByEmailRequestSchema = z.object({
  email: z.string().email(),
  firstName: z.string().max(100).nullable().optional(),
  lastName: z.string().max(100).nullable().optional(),
  roles: z.number().min(1),
  createKeyCloakAccount: z.boolean().optional(),
  sendInvitationEmail: z.boolean().optional(),
});

export type InviteUserByEmailRequest = z.infer<typeof InviteUserByEmailRequestSchema>;

export const UpdateMemberRequestSchema = z.object({
  email: z.string().email().nullable().optional(),
  firstName: z.string().max(100).regex(LATIN_NAME_PATTERN, LATIN_NAME_ERROR).nullable().optional(),
  lastName: z.string().max(100).regex(LATIN_NAME_PATTERN, LATIN_NAME_ERROR).nullable().optional(),
  roles: z.number().nullable().optional(),
  skillLevelId: z.string().uuid().nullable().optional(),
  dateOfBirth: z.string().nullable().optional(),
  bio: z.string().max(500).nullable().optional(),
  photoUrl: z.string().max(2048).nullable().optional(),
  isActive: z.boolean().nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
  gender: z.nativeEnum(Gender).nullable().optional(),
  phoneNumber: z.string().nullable().optional(),
  paymentDueDay: z.number().min(1).max(28).nullable().optional(),
  isBetaTester: z.boolean().nullable().optional(),
});

export type UpdateMemberRequest = z.infer<typeof UpdateMemberRequestSchema>;

export const SchoolPaymentDetailsResponseSchema = z.object({
  schoolId: z.string().uuid(),
  recipientName: z.string(),
  bankName: z.string(),
  accountNumber: z.string(),
  swiftBic: z.string().nullable(),
});

export type SchoolPaymentDetailsResponse = z.infer<typeof SchoolPaymentDetailsResponseSchema>;

export const UpdateSchoolPaymentDetailsRequestSchema = z.object({
  recipientName: z.string().min(1).max(200),
  bankName: z.string().min(1).max(200),
  accountNumber: z.string().min(1).max(34),
  swiftBic: z.string().max(11).nullable().optional(),
});

export type UpdateSchoolPaymentDetailsRequest = z.infer<typeof UpdateSchoolPaymentDetailsRequestSchema>;

export const SchoolDomainResponseSchema = z.object({
  id: z.string().uuid(),
  domain: z.string(),
  isVerified: z.boolean(),
  isActive: z.boolean(),
  createdAt: z.string().datetime(),
  verifiedAt: z.string().datetime().nullable(),
});

export type SchoolDomainResponse = z.infer<typeof SchoolDomainResponseSchema>;

export const RegisterDomainRequestSchema = z.object({
  domain: z.string().min(1).max(253).regex(/^[a-z0-9]([a-z0-9\-\.]{0,251}[a-z0-9])?$/),
});

export type RegisterDomainRequest = z.infer<typeof RegisterDomainRequestSchema>;

// ---- Scheduling ----

export const LocationResponseSchema = z.object({
  id: z.string().uuid(),
  schoolId: z.string().uuid(),
  name: z.string(),
  alias: z.string(),
  address: z.string(),
  district: z.string(),
  postalCode: z.string(),
  city: z.string(),
  courtCount: z.number(),
  maxParticipants: z.number(),
  isActive: z.boolean(),
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
  isIndividualOnly: z.boolean().optional(),
  defaultCourtBookingMode: z.enum(['SchoolReserved', 'SelfBooked']).optional(),
  courtBookingUrl: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
});

export type LocationResponse = z.infer<typeof LocationResponseSchema>;

export const CreateLocationRequestSchema = z.object({
  name: z.string().min(1, 'Name is required').max(200),
  alias: z.string().min(1, 'Alias is required').max(5, 'Alias must be at most 5 characters').regex(/^[a-zA-Z0-9]+$/, 'Alias must contain only letters and numbers'),
  address: z.string().min(1, 'Address is required').max(500),
  district: z.string().max(200),
  postalCode: z.string().min(1, 'Postal code is required').max(20),
  city: z.string().min(1, 'City is required').max(100),
  courtCount: z.number().min(1).max(100),
  maxParticipants: z.number().min(1).max(500),
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
  isIndividualOnly: z.boolean().optional(),
  defaultCourtBookingMode: z.enum(['SchoolReserved', 'SelfBooked']).optional(),
  courtBookingUrl: z.string().max(1000).nullable().optional(),
  description: z.string().max(20000).nullable().optional(),
});

export type CreateLocationRequest = z.infer<typeof CreateLocationRequestSchema>;

export const UpdateLocationRequestSchema = CreateLocationRequestSchema;
export type UpdateLocationRequest = z.infer<typeof UpdateLocationRequestSchema>;

export const ScheduleResponseSchema = z.object({
  id: z.string().uuid(),
  schoolId: z.string().uuid(),
  name: z.string(),
  locationId: z.string().uuid(),
  coachMemberId: z.string().uuid().nullable(),
  secondCoachMemberId: z.string().uuid().nullable().optional(),
  priceListId: z.string().uuid().nullable().optional(),
  skillLevelId: z.string().uuid(),
  skillLevelName: z.string().nullable().optional(),
  dayOfWeek: z.number().min(0).max(6),
  startTime: z.string(), // TimeOnly serialized
  duration: z.number().int().positive(),
  equipmentType: z.nativeEnum(EquipmentType),
  maxParticipants: z.number(),
  isActive: z.boolean(),
  startDate: z.string(),
  endDate: z.string().nullable().optional(),
  recurrencePattern: z.string(),
  isPromoted: z.boolean(),
  isPrivate: z.boolean(),
  isEnrollmentBlocked: z.boolean(),
  isCoursePaidOnce: z.boolean(),
  bannerUrl: z.string().nullable().optional(),
  infoUrl: z.string().nullable().optional(),
  additionalInformation: z.string().max(500).nullable().optional(),
  customDropInPrice: z.number().nullable().optional(),
  customMonthlyPrice: z.number().nullable().optional(),
  customEquipmentSurcharge: z.number().nullable().optional(),
  corporateSlotsCount: z.number().optional().default(0),
  activeSubscribersCount: z.number().optional().default(0),
  disableMakeups: z.boolean().default(false),
  tags: z.array(z.string()).default([]),
  availableSpots: z.number().default(0),
  isFull: z.boolean().default(false),
});

export type ScheduleResponse = z.infer<typeof ScheduleResponseSchema>;

export const CreateScheduleRequestSchema = z.object({
  name: z.string().max(100).nullable().optional(),
  locationId: z.string().uuid(),
  coachMemberId: z.string().uuid().nullable().optional().or(z.literal('')),
  secondCoachMemberId: z.string().uuid().nullable().optional().or(z.literal('')),
  priceListId: z.string().uuid().nullable().optional().or(z.literal('')),
  skillLevelId: z.string().uuid(),
  dayOfWeek: z.number().min(0).max(6),
  startTime: z.string(), // HH:mm
  duration: z.number().int().positive(),
  equipmentType: z.nativeEnum(EquipmentType),
  maxParticipants: z.number().min(1).max(500),
  startDate: z.string(),
  endDate: z.string().nullable().optional(),
  recurrencePattern: z.string().default('Weekly'),
  isPromoted: z.boolean().default(false),
  isPrivate: z.boolean().default(false),
  isCoursePaidOnce: z.boolean().default(false),
  bannerUrl: z.string().max(1000).nullable().optional(),
  infoUrl: z.string().max(1000).nullable().optional(),
  additionalInformation: z.string().max(500).nullable().optional(),
  customDropInPrice: z.number().min(0).nullable().optional(),
  customMonthlyPrice: z.number().min(0).nullable().optional(),
  customEquipmentSurcharge: z.number().min(0).nullable().optional(),
  corporateSlotsCount: z.number().int().min(0),
  disableMakeups: z.boolean().optional(),
  tags: z.array(z.string()).optional(),
}).refine((data) => !data.endDate || data.startDate <= data.endDate, {
  message: 'Data zakończenia grupy nie może być wcześniejsza niż data rozpoczęcia',
  path: ['endDate'],
});

export type CreateScheduleRequest = z.infer<typeof CreateScheduleRequestSchema>;

export const UpdateScheduleRequestSchema = z.object({
  name: z.string().max(100).nullable().optional(),
  locationId: z.string().uuid(),
  coachMemberId: z.string().uuid().nullable().optional().or(z.literal('')),
  secondCoachMemberId: z.string().uuid().nullable().optional().or(z.literal('')),
  priceListId: z.string().uuid().nullable().optional().or(z.literal('')),
  skillLevelId: z.string().uuid(),
  dayOfWeek: z.number().min(0).max(6),
  startTime: z.string(),
  duration: z.number().int().positive(),
  equipmentType: z.nativeEnum(EquipmentType),
  maxParticipants: z.number().min(1).max(500),
  startDate: z.string(),
  endDate: z.string().nullable().optional(),
  recurrencePattern: z.string(),
  isPromoted: z.boolean().default(false),
  isPrivate: z.boolean().default(false),
  isCoursePaidOnce: z.boolean().default(false),
  bannerUrl: z.string().max(1000).nullable().optional(),
  infoUrl: z.string().max(1000).nullable().optional(),
  additionalInformation: z.string().max(500).nullable().optional(),
  customDropInPrice: z.number().min(0).nullable().optional(),
  customMonthlyPrice: z.number().min(0).nullable().optional(),
  customEquipmentSurcharge: z.number().min(0).nullable().optional(),
  corporateSlotsCount: z.number().int().min(0),
  disableMakeups: z.boolean().optional(),
  tags: z.array(z.string()).optional(),
  terminateStudentSubscriptions: z.boolean().optional(),
  deleteFutureSessionsAfterEndDate: z.boolean().optional(),
}).refine((data) => !data.endDate || data.startDate <= data.endDate, {
  message: 'Data zakończenia grupy nie może być wcześniejsza niż data rozpoczęcia',
  path: ['endDate'],
});

export type UpdateScheduleRequest = z.infer<typeof UpdateScheduleRequestSchema>;

export const CreateMaintenancePeriodRequestSchema = z.object({
  startDate: z.string(),
  endDate: z.string(),
  reason: z.string().max(200).optional().nullable(),
});

export type CreateMaintenancePeriodRequest = z.infer<typeof CreateMaintenancePeriodRequestSchema>;

export const MaintenancePeriodResponseSchema = z.object({
  id: z.string().uuid(),
  locationId: z.string().uuid(),
  startDate: z.string(),
  endDate: z.string(),
  reason: z.string().nullable().optional(),
});

export type MaintenancePeriodResponse = z.infer<typeof MaintenancePeriodResponseSchema>;

export const SessionNavigationInfoSchema = z.object({
  prevSessionId: z.string().uuid().nullable().optional(),
  prevSessionStartsAt: z.string().datetime().nullable().optional(),
  nextSessionId: z.string().uuid().nullable().optional(),
  nextSessionStartsAt: z.string().datetime().nullable().optional(),
  currentIndex: z.number(),
  totalCount: z.number(),
});

export type SessionNavigationInfo = z.infer<typeof SessionNavigationInfoSchema>;

export const SessionResponseSchema = z.object({
  id: z.string().uuid(),
  schoolId: z.string().uuid(),
  locationId: z.string().uuid(),
  scheduleId: z.string().uuid().nullable(),
  coachMemberId: z.string().uuid().nullable(),
  secondCoachMemberId: z.string().uuid().nullable().optional(),
  type: z.nativeEnum(SessionType),
  status: z.nativeEnum(SessionStatus),
  skillLevelId: z.string().uuid(),
  skillLevelName: z.string().nullable().optional(),
  equipmentType: z.nativeEnum(EquipmentType),
  startsAt: z.string().datetime(),
  endsAt: z.string().datetime(),
  maxParticipants: z.number(),
  enrolledCount: z.number(),
  dropInPrice: z.number(),
  equipmentSurcharge: z.number(),
  title: z.string(),
  topic: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  isPromoted: z.boolean(),
  isPrivate: z.boolean(),
  isEnrollmentBlocked: z.boolean(),
  bannerUrl: z.string().nullable().optional(),
  infoUrl: z.string().nullable().optional(),
  additionalInformation: z.string().max(500).nullable().optional(),
  summaryNotes: z.string().nullable().optional(),
  cancellationReason: z.string().nullable().optional(),
  corporateSlotsCount: z.number().optional().default(0),
  disableMakeups: z.boolean().default(false),
  tags: z.array(z.string()).default([]),
  availableSpots: z.number().default(0),
  isFull: z.boolean().default(false),
  navigation: SessionNavigationInfoSchema.nullable().optional(),
  /** Students play games instead of the regular training on this session. */
  isGameDay: z.boolean().optional().default(false),
});

export type SessionResponse = z.infer<typeof SessionResponseSchema>;

export const TagResponseSchema = z.object({
  id: z.string().uuid(),
  schoolId: z.string().uuid(),
  name: z.string(),
});

export type TagResponse = z.infer<typeof TagResponseSchema>;

export const CreateOneTimeSessionRequestSchema = z.object({
  locationId: z.string().uuid(),
  coachMemberId: z.string().uuid().nullable().optional().or(z.literal('')),
  secondCoachMemberId: z.string().uuid().nullable().optional().or(z.literal('')),
  skillLevelId: z.string().uuid(),
  equipmentType: z.nativeEnum(EquipmentType),
  startsAt: z.string().datetime(),
  endsAt: z.string().datetime(),
  maxParticipants: z.number().min(1).max(500),
  dropInPrice: z.number().min(0),
  equipmentSurcharge: z.number().min(0),
  title: z.string().max(200).optional().nullable(),
  description: z.string().optional().nullable(),
  isPromoted: z.boolean().default(false),
  bannerUrl: z.string().nullable().optional(),
  infoUrl: z.string().url({ message: 'Niepoprawny adres URL (musi zaczynać się od http/https)' }).or(z.literal('')).optional().nullable(),
  additionalInformation: z.string().max(500).nullable().optional(),
  corporateSlotsCount: z.number().int().min(0),
  disableMakeups: z.boolean().optional(),
  tags: z.array(z.string()).optional(),
});

export type CreateOneTimeSessionRequest = z.infer<typeof CreateOneTimeSessionRequestSchema>;

export const UpdateSessionRequestSchema = CreateOneTimeSessionRequestSchema;
export type UpdateSessionRequest = z.infer<typeof UpdateSessionRequestSchema>;

export const UpdateSessionCoachRequestSchema = z.object({
  coachMemberId: z.string().uuid().nullable().optional().or(z.literal('')),
});

export type UpdateSessionCoachRequest = z.infer<typeof UpdateSessionCoachRequestSchema>;

// ---- Billing ----

const PriceListItemSchema = z.object({
  id: z.string().uuid(),
  priceListId: z.string().uuid(),
  validFrom: z.string(),
  validTo: z.string().nullable().optional(),
  grandfatheringUntil: z.string().nullable().optional(),
  duration: z.number().int().positive(),
  dropInPrice: z.number(),
  monthlyBasePrice: z.number(),
  equipmentSurcharge: z.number(),
  partySizeFrom: z.number().int().nullable().optional(),
  partySizeTo: z.number().int().nullable().optional(),
});

export const PriceListResponseSchema = z.object({
  id: z.string().uuid(),
  schoolId: z.string().uuid(),
  name: z.string(),
  isActive: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  equipmentName: z.string(),
  items: z.array(PriceListItemSchema),
  kind: z.enum(['Group', 'Individual']).optional(),
});

export type PriceListResponse = z.infer<typeof PriceListResponseSchema>;

export const CreatePriceListRequestSchema = z.object({
  name: z.string().min(1).max(200),
  equipmentName: z.string().min(1).max(100).optional().default('Lotki'),
  kind: z.enum(['Group', 'Individual']).optional(),
});

export type CreatePriceListRequest = z.infer<typeof CreatePriceListRequestSchema>;

export const UpdatePriceListRequestSchema = z.object({
  name: z.string().min(1).max(200),
  isActive: z.boolean(),
  equipmentName: z.string().min(1).max(100).optional().default('Lotki'),
});

export type UpdatePriceListRequest = z.infer<typeof UpdatePriceListRequestSchema>;


export const CreatePriceListItemRequestSchema = z.object({
  validFrom: z.string().min(1),
  validTo: z.string().nullable().optional(),
  grandfatheringUntil: z.string().nullable().optional(),
  duration: z.number().int().positive(),
  dropInPrice: z.number().min(0),
  monthlyBasePrice: z.number().min(0),
  equipmentSurcharge: z.number().min(0),
  partySizeFrom: z.number().int().min(1).max(4).nullable().optional(),
  partySizeTo: z.number().int().min(1).max(4).nullable().optional(),
});

export type CreatePriceListItemRequest = z.infer<typeof CreatePriceListItemRequestSchema>;

export const UpdatePriceListItemRequestSchema = z.object({
  validFrom: z.string().nullable().optional(),
  validTo: z.string().nullable().optional(),
  grandfatheringUntil: z.string().nullable().optional(),
  dropInPrice: z.number().min(0).nullable().optional(),
  monthlyBasePrice: z.number().min(0).nullable().optional(),
  equipmentSurcharge: z.number().min(0).nullable().optional(),
  partySizeFrom: z.number().int().min(1).max(4).nullable().optional(),
  partySizeTo: z.number().int().min(1).max(4).nullable().optional(),
});

export type UpdatePriceListItemRequest = z.infer<typeof UpdatePriceListItemRequestSchema>;

export const PaymentResponseSchema = z.object({
  id: z.string().uuid(),
  schoolId: z.string().uuid(),
  studentMemberId: z.string().uuid(),
  amount: z.number(),
  type: z.nativeEnum(PaymentType),
  status: z.nativeEnum(PaymentStatus),
  description: z.string().nullable(),
  recordedAt: z.string().datetime(),
  invoiceId: z.string().uuid().nullable().optional(),
  invoiceNumber: z.string().nullable().optional(),
  externalTransactionId: z.string().nullable().optional(),
  payUrl: z.string().nullable().optional(),
});

export type PaymentResponse = z.infer<typeof PaymentResponseSchema>;

export const SubscriptionResponseSchema = z.object({
  id: z.string().uuid(),
  schoolId: z.string().uuid(),
  studentMemberId: z.string().uuid(),
  scheduleId: z.string().uuid(),
  billingMonth: z.string(), // DateOnly as yyyy-MM-dd
  status: z.nativeEnum(SubscriptionStatus),
  amountCharged: z.number(),
  willNotRenew: z.boolean(),
  createdAt: z.string().optional(),
  firstSessionStartsAt: z.string().nullable().optional(),
  resignationEffectiveDate: z.string().nullable().optional(),
  resignationDate: z.string().nullable().optional(),
  scheduleName: z.string().nullable().optional(),
  isCorporate: z.boolean(),
  studentName: z.string().nullable().optional(),
  startDate: z.string().nullable().optional(),
});

export type SubscriptionResponse = z.infer<typeof SubscriptionResponseSchema>;

export const ResignationPreviewResponseSchema = z.object({
  currentMonth: z.string(),
  nextBillingMonth: z.string(),
  effectiveResignationDate: z.string(),
  isPastDeadline: z.boolean(),
  lastSessionDate: z.string().nullable().optional(),
  resignationRule: z.string(),
});

export type ResignationPreviewResponse = z.infer<typeof ResignationPreviewResponseSchema>;

export const MakeUpCreditResponseSchema = z.object({
  id: z.string().uuid(),
  studentMemberId: z.string().uuid(),
  status: z.nativeEnum(CreditStatus),
  issuedAt: z.string().datetime(),
  expiresAt: z.string().datetime(),
  redeemedForSessionId: z.string().uuid().nullable().optional(),
  redeemedForSessionTitle: z.string().nullable().optional(),
  redeemedForSessionStartsAt: z.string().datetime().nullable().optional(),
  sourceSessionId: z.string().uuid().nullable().optional(),
  sourceSessionTitle: z.string().nullable().optional(),
  sourceSessionStartsAt: z.string().datetime().nullable().optional(),
});

export type MakeUpCreditResponse = z.infer<typeof MakeUpCreditResponseSchema>;

// ---- Coach Availability ----

export const SetDateRangeUnavailabilityRequestSchema = z.object({
  startDate: z.string(), // DateOnly as yyyy-MM-dd
  endDate: z.string(),
  reason: z.string().max(300).nullable().optional(),
});

export type SetDateRangeUnavailabilityRequest = z.infer<typeof SetDateRangeUnavailabilityRequestSchema>;

export const SetRecurringUnavailabilityRequestSchema = z.object({
  dayOfWeek: z.number().min(0).max(6),
  blockStart: z.string(), // HH:mm
  blockEnd: z.string(), // HH:mm
});

export type SetRecurringUnavailabilityRequest = z.infer<typeof SetRecurringUnavailabilityRequestSchema>;

export const CoachAvailabilityResponseSchema = z.object({
  id: z.string().uuid(),
  coachMemberId: z.string().uuid(),
  type: z.nativeEnum(AvailabilityType),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  reason: z.string().nullable(),
  dayOfWeek: z.number().min(0).max(6).nullable(),
  blockStart: z.string().nullable(),
  blockEnd: z.string().nullable(),
  status: z.nativeEnum(AvailabilityStatus),
});

export type CoachAvailabilityResponse = z.infer<typeof CoachAvailabilityResponseSchema>;

export const ConflictingSessionResponseSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  startsAt: z.string(),
  endsAt: z.string(),
  locationName: z.string(),
  coachMemberId: z.string().uuid().nullable(),
  coachName: z.string().nullable(),
  participantCount: z.number(),
});

export type ConflictingSessionResponse = z.infer<typeof ConflictingSessionResponseSchema>;

export const UnavailabilityRequestResponseSchema = z.object({
  id: z.string().uuid(),
  coachMemberId: z.string().uuid(),
  coachName: z.string(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  reason: z.string().nullable(),
  conflictingSessions: z.array(ConflictingSessionResponseSchema),
  createdAt: z.string(),
  status: z.nativeEnum(AvailabilityStatus),
});

export type UnavailabilityRequestResponse = z.infer<typeof UnavailabilityRequestResponseSchema>;

export const RespondToUnavailabilityRequestSchema = z.object({
  approve: z.boolean(),
});

export type RespondToUnavailabilityRequest = z.infer<typeof RespondToUnavailabilityRequestSchema>;

// ---- Notifications ----

export const NotificationResponseSchema = z.object({
  id: z.string().uuid(),
  schoolId: z.string().uuid(),
  userId: z.string().uuid(),
  title: z.string(),
  body: z.string(),
  payload: z.string().nullable(),
  isRead: z.boolean(),
  showOnDashboard: z.boolean(),
  createdAt: z.string().datetime(),
  readAt: z.string().datetime().nullable(),
});

export type NotificationResponse = z.infer<typeof NotificationResponseSchema>;

export const SendCustomNotificationRequestSchema = z.object({
  title: z.string().min(1),
  body: z.string().min(1),
  payload: z.string().nullable().optional(),
});

export type SendCustomNotificationRequest = z.infer<typeof SendCustomNotificationRequestSchema>;

export const SendBatchNotificationRequestSchema = z.object({
  memberIds: z.array(z.string().uuid()),
  title: z.string().min(1),
  body: z.string().min(1),
  payload: z.string().nullable().optional(),
});

export type SendBatchNotificationRequest = z.infer<typeof SendBatchNotificationRequestSchema>;

export const RegisterPushTokenRequestSchema = z.object({
  token: z.string().min(1).max(255),
  platform: z.string().max(50).optional(),
  deviceInfo: z.string().max(255).nullable().optional(),
});

export type RegisterPushTokenRequest = z.infer<typeof RegisterPushTokenRequestSchema>;

export const DevicePushTokenResponseSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  schoolId: z.string().uuid().nullable().optional(),
  token: z.string(),
  platform: z.string(),
  deviceInfo: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type DevicePushTokenResponse = z.infer<typeof DevicePushTokenResponseSchema>;


// ---- Enrollment ----

export const EnrollmentSessionResponseSchema = z.object({
  id: z.string().uuid(),
  startsAt: z.string(),
  title: z.string().nullable(),
  skillLevelName: z.string().nullable(),
  locationId: z.string().uuid(),
  type: z.string(),
  isScheduleActive: z.boolean(),
  status: z.nativeEnum(SessionStatus),
  scheduleId: z.string().uuid().nullable().optional(),
  dropInPrice: z.number().optional(),
  equipmentSurcharge: z.number().optional(),
  coachName: z.string().nullable().optional(),
  dayOfWeek: z.number().nullable().optional(),
  startTime: z.string().nullable().optional(),
  duration: z.number().nullable().optional(),
});

export type EnrollmentSessionResponse = z.infer<typeof EnrollmentSessionResponseSchema>;

/** The other end of a one-off session transfer of an enrollment. */
export const EnrollmentTransferInfoSchema = z.object({
  sessionId: z.string().uuid(),
  startsAt: z.string().datetime(),
  locationName: z.string().nullable().optional(),
});

export type EnrollmentTransferInfo = z.infer<typeof EnrollmentTransferInfoSchema>;

export const EnrollmentResponseSchema = z.object({
  id: z.string().uuid(),
  schoolId: z.string().uuid(),
  sessionId: z.string().uuid(),
  studentMemberId: z.string().uuid(),
  type: z.nativeEnum(EnrollmentType),
  status: z.nativeEnum(EnrollmentStatus),
  isOverbooking: z.boolean(),
  makeUpCreditId: z.string().uuid().nullable(),
  enrolledAt: z.string().datetime(),
  paymentId: z.string().uuid().nullable().optional(),
  isApproved: z.boolean(),
  session: EnrollmentSessionResponseSchema.nullable().optional(),
  attendanceStatus: z.nativeEnum(AttendanceStatus).nullable().optional(),
  studentName: z.string().nullable().optional(),
  transferredFrom: EnrollmentTransferInfoSchema.nullable().optional(),
  transferredTo: EnrollmentTransferInfoSchema.nullable().optional(),
  /** Last suspended day (`yyyy-MM-dd`) of a group member suspended on the session date. */
  suspendedUntil: z.string().nullable().optional(),
  suspensionReason: z.string().nullable().optional(),
});

export type EnrollmentResponse = z.infer<typeof EnrollmentResponseSchema>;


export const EnrollmentListResponseSchema = z.object({
  items: z.array(EnrollmentResponseSchema),
  totalCount: z.number(),
});

export type EnrollmentListResponse = z.infer<typeof EnrollmentListResponseSchema>;

export const EnrollStudentRequestSchema = z.object({
  sessionId: z.string().uuid(),
  studentMemberId: z.string().uuid(),
  type: z.nativeEnum(EnrollmentType),
  makeUpCreditId: z.string().uuid().nullable().optional(),
  paymentType: z.nativeEnum(PaymentType).nullable().optional(),
  discountCouponId: z.string().uuid().nullable().optional(),
  isZeroPayment: z.boolean().optional(),
  useMakeUpCredit: z.boolean().optional(),
  useGiftVouchers: z.boolean().optional(),
});

export type EnrollStudentRequest = z.infer<typeof EnrollStudentRequestSchema>;

export const MarkAttendanceRequestSchema = z.object({
  enrollmentId: z.string().uuid(),
  status: z.nativeEnum(AttendanceStatus),
});

export type MarkAttendanceRequest = z.infer<typeof MarkAttendanceRequestSchema>;

export const SetStudentSkillLevelRequestSchema = z.object({
  studentMemberId: z.string().uuid(),
  skillLevelId: z.string().uuid().nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
});

export type SetStudentSkillLevelRequest = z.infer<typeof SetStudentSkillLevelRequestSchema>;

export const TransferStudentGroupRequestSchema = z.object({
  targetScheduleId: z.string().uuid(),
  allowOverbooking: z.boolean().optional(),
});

export type TransferStudentGroupRequest = z.infer<typeof TransferStudentGroupRequestSchema>;

export const BulkTransferStudentGroupRequestSchema = z.object({
  studentMemberIds: z.array(z.string().uuid()),
  targetScheduleId: z.string().uuid(),
  allowOverbooking: z.boolean().optional(),
});

export type BulkTransferStudentGroupRequest = z.infer<typeof BulkTransferStudentGroupRequestSchema>;

export const TransferStudentGroupNextMonthRequestSchema = z.object({
  studentMemberId: z.string().uuid(),
  sourceScheduleId: z.string().uuid(),
  targetScheduleId: z.string().uuid(),
  allowOverbooking: z.boolean().optional(),
});

export type TransferStudentGroupNextMonthRequest = z.infer<typeof TransferStudentGroupNextMonthRequestSchema>;

// ── Attachments ────────────────────────────────────────────────────────────────

export const AttachmentResponseSchema = z.object({
  id: z.string().uuid(),
  schoolId: z.string().uuid(),
  fileName: z.string(),
  contentType: z.string(),
  size: z.union([z.number(), z.string()]),
  publicUrl: z.string(),
  createdAt: z.string().datetime(),
  title: z.string().nullable().optional(),
  altText: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  mediaType: z.enum(['Image', 'ExternalVideo', 'Document']).optional(),
  externalUrl: z.string().nullable().optional(),
  virtualFolder: z.string().nullable().optional(),
});

export type AttachmentResponse = z.infer<typeof AttachmentResponseSchema>;


// ── Skill Levels ────────────────────────────────────────────────────────────────

export const CreateSkillLevelRequestSchema = z.object({
  namePl: z.string().min(1, 'Name (PL) is required').max(50, 'Name must be 50 characters or less'),
  nameEn: z.string().min(1, 'Name (EN) is required').max(50, 'Name must be 50 characters or less'),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Color must be a valid hex color (e.g., #FF5733)'),
  category: z.nativeEnum(SkillLevelCategory).optional(),
});

export type CreateSkillLevelRequest = z.infer<typeof CreateSkillLevelRequestSchema>;

export const UpdateSkillLevelRequestSchema = CreateSkillLevelRequestSchema;
export type UpdateSkillLevelRequest = z.infer<typeof UpdateSkillLevelRequestSchema>;

export const ReorderSkillLevelsRequestSchema = z.object({
  skillLevelIds: z.array(z.string().uuid()),
});

export type ReorderSkillLevelsRequest = z.infer<typeof ReorderSkillLevelsRequestSchema>;

// ── Backward-compatible aliases ────────────────────────────────────────────────

/** @deprecated Use MemberResponseSchema */
export const SchoolMemberResponseSchema = MemberResponseSchema;
/** @deprecated Use MemberResponse */
export type SchoolMemberResponse = MemberResponse;
/** @deprecated Use UpdateMemberRequestSchema */
export const UpdateSchoolMemberRequestSchema = UpdateMemberRequestSchema;
/** @deprecated Use UpdateMemberRequest */
export type UpdateSchoolMemberRequest = UpdateMemberRequest;

// ── Coach Payment ─────────────────────────────────────────────────────────────
export const RecordCoachPaymentRequestSchema = z.object({
  studentMemberId: z.string().uuid(),
  amount: z.number().positive('Kwota musi być większa od zera'),
  description: z.string().min(1, 'Cel wpłaty jest wymagany').max(200, 'Maksymalnie 200 znaków'),
});

export type RecordCoachPaymentRequest = z.infer<typeof RecordCoachPaymentRequestSchema>;

// ── Children ─────────────────────────────────────────────────────────────────
export const CreateChildRequestSchema = z.object({
  firstName: z.string().min(1, 'First name is required').max(100),
  lastName: z.string().min(1, 'Last name is required').max(100),
  dateOfBirth: z.string().min(1, 'Date of birth is required'),
  gender: z.nativeEnum(Gender),
  skillLevelId: z.string().uuid().nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
});

export type CreateChildRequest = z.infer<typeof CreateChildRequestSchema>;

// ---- Regulations ----

export const DefineRegulationRequestSchema = z.object({
  title: z.string().min(1, 'Title is required').max(200),
  content: z.string().min(1, 'Content is required'),
  introMessage: z.string().max(2000).nullable().optional(),
  publishedAt: z.string().datetime(),
  effectiveAt: z.string().datetime(),
  language: z.string().default('pl'),
  type: z.enum(['General', 'IndividualClasses']).optional(),
});

export type DefineRegulationRequest = z.infer<typeof DefineRegulationRequestSchema>;

export const UpdateRegulationRequestSchema = z.object({
  title: z.string().min(1, 'Title is required').max(200),
  content: z.string().min(1, 'Content is required'),
  introMessage: z.string().max(2000).nullable().optional(),
  publishedAt: z.string().datetime(),
  effectiveAt: z.string().datetime(),
  language: z.string().default('pl'),
  type: z.enum(['General', 'IndividualClasses']).optional(),
});

export type UpdateRegulationRequest = z.infer<typeof UpdateRegulationRequestSchema>;


export const RegulationResponseSchema = z.object({
  id: z.string().uuid(),
  schoolId: z.string().uuid(),
  language: z.string(),
  title: z.string(),
  content: z.string(),
  introMessage: z.string().nullable(),
  publishedAt: z.string().datetime(),
  effectiveAt: z.string().datetime(),
  isNotified: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  type: z.enum(['General', 'IndividualClasses']).optional(),
});

export type RegulationResponse = z.infer<typeof RegulationResponseSchema>;



// ---- Discount Coupons ----

export const DiscountCouponUsageResponseSchema = z.object({
  id: z.string().uuid(),
  discountCouponId: z.string().uuid(),
  studentMemberId: z.string().uuid(),
  studentName: z.string().nullable().optional(),
  amountUsed: z.number(),
  usedAt: z.string().datetime(),
  enrollmentId: z.string().uuid().nullable().optional(),
  billId: z.string().uuid().nullable().optional(),
});

export type DiscountCouponUsageResponse = z.infer<typeof DiscountCouponUsageResponseSchema>;

export const DiscountCouponResponseSchema = z.object({
  id: z.string().uuid(),
  schoolId: z.string().uuid(),
  code: z.string(),
  couponType: z.nativeEnum(CouponType).optional().default(CouponType.Amount),
  discountPercent: z.number(),
  initialAmount: z.number().optional().default(0),
  remainingAmount: z.number().optional().default(0),
  status: z.nativeEnum(DiscountCouponStatus),
  studentMemberId: z.string().uuid().nullable().optional(),
  studentName: z.string().nullable().optional(),
  createdAt: z.string().datetime(),
  expiresAt: z.string().datetime(),
  usedForEnrollmentId: z.string().uuid().nullable().optional(),
  usedForBillId: z.string().uuid().nullable().optional(),
  redeemedAt: z.string().datetime().nullable().optional(),
  usedAt: z.string().datetime().nullable().optional(),
  usages: z.array(DiscountCouponUsageResponseSchema).nullable().optional(),
});

export type DiscountCouponResponse = z.infer<typeof DiscountCouponResponseSchema>;

export const GenerateCouponsRequestSchema = z.object({
  couponType: z.nativeEnum(CouponType),
  discountPercent: z.number().min(0).max(100),
  initialAmount: z.number().min(0),
  quantity: z.number().int().positive(),
  expiresAt: z.string().refine((val) => !isNaN(Date.parse(val)), {
    message: 'Invalid datetime',
  }),
  studentMemberId: z.string().uuid().nullable().optional(),
  customCode: z.string().max(50).nullable().optional(),
});

export type GenerateCouponsRequest = z.infer<typeof GenerateCouponsRequestSchema>;

export const RedeemCouponRequestSchema = z.object({
  code: z.string().min(1),
  studentMemberId: z.string().uuid().nullable().optional(),
});

export type RedeemCouponRequest = z.infer<typeof RedeemCouponRequestSchema>;

export const IssueManualCreditRequestSchema = z.object({
  skillLevelId: z.string().uuid(),
});

export type IssueManualCreditRequest = z.infer<typeof IssueManualCreditRequestSchema>;

export const ClaimWaitlistResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  enrollmentId: z.string().uuid().nullable().optional(),
  monthlySubscriptionId: z.string().uuid().nullable().optional(),
  paymentId: z.string().uuid().nullable().optional(),
});

export type ClaimWaitlistResponse = z.infer<typeof ClaimWaitlistResponseSchema>;

// ---- Schedule Enrollment (Group contract) ----

export const EnrollStudentGroupRequestSchema = z.object({
  studentMemberId: z.string().uuid(),
  scheduleId: z.string().uuid(),
  startDate: z.string(), // ISO Date string (YYYY-MM-DD)
  paymentType: z.nativeEnum(PaymentType).nullable().optional(),
  markAsPaid: z.boolean().optional(),
  isCorporate: z.boolean().optional(),
  allowOverbooking: z.boolean().optional(),
});

export type EnrollStudentGroupRequest = z.infer<typeof EnrollStudentGroupRequestSchema>;

export const ResignStudentGroupRequestSchema = z.object({
  studentMemberId: z.string().uuid(),
  scheduleId: z.string().uuid(),
  resignationDate: z.string().nullable().optional(),
});

export type ResignStudentGroupRequest = z.infer<typeof ResignStudentGroupRequestSchema>;

export const UpdateSubscriptionEndDateRequestSchema = z.object({
  studentMemberId: z.string().uuid(),
  scheduleId: z.string().uuid(),
  endDate: z.string().nullable().optional(),
});

export type UpdateSubscriptionEndDateRequest = z.infer<typeof UpdateSubscriptionEndDateRequestSchema>;

export const ScheduleEnrollmentResponseSchema = z.object({
  id: z.string().uuid(),
  schoolId: z.string().uuid(),
  studentMemberId: z.string().uuid(),
  scheduleId: z.string().uuid(),
  startDate: z.string(),
  resignationDate: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  studentFirstName: z.string().nullable().optional(),
  studentLastName: z.string().nullable().optional(),
  studentEmail: z.string().nullable().optional(),
  isApproved: z.boolean(),
  isCorporate: z.boolean(),
});

export type ScheduleEnrollmentResponse = z.infer<typeof ScheduleEnrollmentResponseSchema>;

export const StudentNegativeBalanceDtoSchema = z.object({
  studentMemberId: z.string().uuid(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  phoneNumber: z.string().nullable().optional(),
  balance: z.number(),
  totalCharged: z.number(),
  totalPaid: z.number(),
  unpaidBillsCount: z.number(),
  lastPaymentDate: z.string().nullable().optional(),
  lastBillDate: z.string().nullable().optional(),
});

export type StudentNegativeBalanceDto = z.infer<typeof StudentNegativeBalanceDtoSchema>;

export const StudentNegativeBalancesResponseSchema = z.object({
  items: z.array(StudentNegativeBalanceDtoSchema),
  totalCount: z.number(),
  totalDebt: z.number(),
});

export type StudentNegativeBalancesResponse = z.infer<typeof StudentNegativeBalancesResponseSchema>;

export interface GetStudentNegativeBalancesRequest {
  query?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDesc?: boolean;
}

export const CreateSubscriptionSuspensionRequestSchema = z.object({
  startDate: z.string(),
  endDate: z.string(),
  reason: z.string().nullable().optional(),
});

export type CreateSubscriptionSuspensionRequest = z.infer<typeof CreateSubscriptionSuspensionRequestSchema>;

export const UpdateSubscriptionSuspensionRequestSchema = z.object({
  startDate: z.string(),
  endDate: z.string(),
  reason: z.string().nullable().optional(),
});

export type UpdateSubscriptionSuspensionRequest = z.infer<typeof UpdateSubscriptionSuspensionRequestSchema>;

export const SubscriptionSuspensionResponseSchema = z.object({
  id: z.string().uuid(),
  schoolId: z.string().uuid(),
  studentMemberId: z.string().uuid(),
  scheduleEnrollmentId: z.string().uuid(),
  scheduleId: z.string().uuid(),
  startDate: z.string(),
  endDate: z.string(),
  reason: z.string().nullable().optional(),
  status: z.enum(['Active', 'Cancelled', 'Completed']),
  reminderSent: z.boolean(),
  createdByUserId: z.string().uuid().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  scheduleName: z.string().nullable().optional(),
});

export type SubscriptionSuspensionResponse = z.infer<typeof SubscriptionSuspensionResponseSchema>;

// ---- Promos / Banners ----

export const PromoResponseSchema = z.object({
  id: z.string().uuid(),
  schoolId: z.string().uuid(),
  displayKey: z.string().uuid(),
  title: z.string(),
  imageUrl: z.string(),
  targetUrl: z.string().nullable().optional(),
  publishStartDate: z.string(),
  publishEndDate: z.string().nullable().optional(),
  isActive: z.boolean(),
  isCurrentlyPublished: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type PromoResponse = z.infer<typeof PromoResponseSchema>;

export const CreatePromoRequestSchema = z.object({
  title: z.string().min(1, 'Title is required').max(300),
  imageUrl: z.string().min(1, 'Image is required'),
  targetUrl: z.string().nullable().optional(),
  publishStartDate: z.string(),
  publishEndDate: z.string().nullable().optional(),
  isActive: z.boolean().default(true),
});

export type CreatePromoRequest = z.infer<typeof CreatePromoRequestSchema>;

export const UpdatePromoRequestSchema = z.object({
  title: z.string().min(1, 'Title is required').max(300),
  imageUrl: z.string().min(1, 'Image is required'),
  targetUrl: z.string().nullable().optional(),
  publishStartDate: z.string(),
  publishEndDate: z.string().nullable().optional(),
  isActive: z.boolean(),
});

export type UpdatePromoRequest = z.infer<typeof UpdatePromoRequestSchema>;


// ---- Individual classes ----

export const CourtBookingModeSchema = z.enum(['SchoolReserved', 'SelfBooked']);
export type CourtBookingMode = z.infer<typeof CourtBookingModeSchema>;
export const IndividualPaymentMethodSchema = z.enum(['Online', 'OnSite']);
export type IndividualPaymentMethod = z.infer<typeof IndividualPaymentMethodSchema>;
export const IndividualBookingTypeSchema = z.enum(['Single', 'FixedSlot']);
export type IndividualBookingType = z.infer<typeof IndividualBookingTypeSchema>;
export type BookingApprovalStatus = 'NotRequired' | 'Pending' | 'Approved' | 'Rejected' | 'Expired';
export type OnSitePaymentMethod = 'Cash' | 'Blik';

export interface IndividualClassSettingsResponse {
  isEnabled: boolean;
  individualPriceListId: string | null;
  blikPhoneNumber: string | null;
  freeCancellationHours: number;
  approvalTimeoutHours: number;
  bookingMinLeadHours: number;
  bookingHorizonDays: number;
  canManageSlots: boolean;
  isAdmin: boolean;
  currentRegulationId: string | null;
}

export const UpdateIndividualClassSettingsRequestSchema = z.object({
  individualPriceListId: z.string().uuid().nullable(),
  blikPhoneNumber: z.string().max(30).nullable(),
  freeCancellationHours: z.number().int().min(0).max(168),
  approvalTimeoutHours: z.number().int().min(1).max(168),
  bookingMinLeadHours: z.number().int().min(0).max(168),
  bookingHorizonDays: z.number().int().min(1).max(365),
});
export type UpdateIndividualClassSettingsRequest = z.infer<typeof UpdateIndividualClassSettingsRequestSchema>;

export interface IndividualCoachResponse {
  memberId: string;
  fullName: string;
  canOfferIndividualClasses: boolean;
}

export interface IndividualPriceTier {
  partySizeFrom: number;
  partySizeTo: number;
  singlePrice: number;
  fixedSlotPrice: number;
}

export interface CompanionDto {
  firstName: string | null;
  lastName: string | null;
}

export interface IndividualBookingSummary {
  bookingId: string;
  bookingType: IndividualBookingType;
  studentMemberId: string;
  studentName: string;
  studentEmail: string | null;
  studentPhone: string | null;
  partySize: number;
  companions: CompanionDto[];
  paymentMethod: IndividualPaymentMethod | null;
  approvalStatus: BookingApprovalStatus;
  approvalDeadline: string | null;
  courtSelfBookingConfirmedAt: string | null;
  createdAt: string;
}

export interface IndividualSlotResponse {
  sessionId: string;
  scheduleId: string | null;
  coachMemberId: string | null;
  coachName: string;
  locationId: string;
  locationName: string;
  locationAddress: string | null;
  startsAt: string;
  endsAt: string;
  duration: number;
  maxPartySize: number;
  courtBookingMode: CourtBookingMode;
  courtBookingUrl: string | null;
  allowOnlinePayment: boolean;
  allowOnSitePayment: boolean;
  isRecurring: boolean;
  isFixedSlotAvailable: boolean;
  additionalInformation: string | null;
  status: 'Scheduled' | 'Cancelled' | 'Completed';
  isBooked: boolean;
  priceTiers: IndividualPriceTier[];
  booking: IndividualBookingSummary | null;
}

export interface IndividualPricePreviewResponse {
  partySize: number;
  bookingType: IndividualBookingType;
  pricePerSession: number;
  firstMonthSessionCount: number;
  estimatedTotal: number;
}

export interface IndividualScheduleResponse {
  scheduleId: string;
  coachMemberId: string | null;
  coachName: string;
  locationId: string;
  locationName: string;
  dayOfWeek: string;
  startTime: string;
  duration: number;
  startDate: string;
  endDate: string | null;
  maxPartySize: number;
  courtBookingMode: CourtBookingMode | null;
  courtBookingUrl: string | null;
  allowOnlinePayment: boolean;
  allowOnSitePayment: boolean;
  allowFixedSlot: boolean;
  additionalInformation: string | null;
  activeFixedSlots: number;
}

export const CreateIndividualSlotRequestSchema = z
  .object({
    coachMemberId: z.string().uuid().nullable().optional(),
    locationId: z.string().uuid(),
    isRecurring: z.boolean(),
    startsAt: z.string().nullable().optional(),
    dayOfWeek: z.string().nullable().optional(),
    startTime: z.string().nullable().optional(),
    startDate: z.string().nullable().optional(),
    endDate: z.string().nullable().optional(),
    duration: z.number().int().min(15).max(480),
    maxPartySize: z.number().int().min(1).max(4),
    courtBookingMode: CourtBookingModeSchema.nullable().optional(),
    courtBookingUrl: z.string().max(1000).nullable().optional(),
    allowOnlinePayment: z.boolean(),
    allowOnSitePayment: z.boolean(),
    allowFixedSlot: z.boolean(),
    additionalInformation: z.string().max(500).nullable().optional(),
  })
  .refine((v) => v.allowOnlinePayment || v.allowOnSitePayment, { path: ['allowOnSitePayment'], message: 'paymentMethodRequired' })
  .refine((v) => v.isRecurring || !!v.startsAt, { path: ['startsAt'], message: 'required' })
  .refine((v) => !v.isRecurring || (!!v.dayOfWeek && !!v.startTime), { path: ['startTime'], message: 'required' });
export type CreateIndividualSlotRequest = z.infer<typeof CreateIndividualSlotRequestSchema>;

export interface UpdateIndividualSlotRequest {
  locationId: string;
  startsAt: string;
  duration: number;
  maxPartySize: number;
  courtBookingMode: CourtBookingMode | null;
  courtBookingUrl: string | null;
  allowOnlinePayment: boolean;
  allowOnSitePayment: boolean;
  additionalInformation: string | null;
}

/**
 * Booking form. The court self-booking confirmation is required only for slots where the student books the court,
 * the regulation consent is always required.
 */
export const createIndividualBookingFormSchema = (courtMode: CourtBookingMode) =>
  z
    .object({
      bookingType: IndividualBookingTypeSchema,
      partySize: z.number().int().min(1).max(4),
      companions: z.array(z.object({ firstName: z.string().max(100), lastName: z.string().max(100) })),
      paymentMethod: IndividualPaymentMethodSchema,
      acceptRegulation: z.boolean().refine((v) => v, { message: 'regulationRequired' }),
      confirmCourtSelfBooking: z.boolean(),
    })
    .refine((v) => courtMode !== 'SelfBooked' || v.confirmCourtSelfBooking, {
      path: ['confirmCourtSelfBooking'],
      message: 'courtConfirmationRequired',
    });
export type IndividualBookingForm = z.infer<ReturnType<typeof createIndividualBookingFormSchema>>;

export interface CreateIndividualBookingRequest {
  sessionId: string;
  bookingType: IndividualBookingType;
  partySize: number;
  companions: CompanionDto[];
  paymentMethod: IndividualPaymentMethod;
  regulationId: string;
  acceptRegulation: boolean;
  confirmCourtSelfBooking: boolean;
  studentMemberId?: string | null;
}

export interface IndividualOccurrenceResponse {
  enrollmentId: string;
  sessionId: string;
  startsAt: string;
  endsAt: string;
  status: 'Active' | 'Cancelled' | 'Absent';
  isLateCancellation: boolean;
  canCancelFree: boolean;
  lateCancellationFee: number;
}

export interface IndividualBillInfo {
  billId: string;
  amount: number;
  status: 'Pending' | 'PartiallyPaid' | 'Paid' | 'Cancelled';
  description: string;
  billingMonth: string | null;
}

export interface MyIndividualBookingResponse {
  bookingId: string;
  bookingType: IndividualBookingType;
  studentMemberId: string;
  studentName: string;
  sessionId: string | null;
  scheduleId: string | null;
  coachName: string;
  locationName: string;
  locationAddress: string | null;
  startsAt: string | null;
  endsAt: string | null;
  dayOfWeek: string | null;
  startTime: string | null;
  duration: number;
  partySize: number;
  companions: CompanionDto[];
  paymentMethod: IndividualPaymentMethod | null;
  approvalStatus: BookingApprovalStatus;
  rejectionReason: string | null;
  status: 'Active' | 'Cancelled' | 'Absent' | null;
  isLateCancellation: boolean;
  courtBookingMode: CourtBookingMode;
  courtBookingUrl: string | null;
  pricePerSession: number;
  canCancelFree: boolean;
  lateCancellationFee: number;
  resignationDate: string | null;
  bills: IndividualBillInfo[];
  upcomingOccurrences: IndividualOccurrenceResponse[];
  createdAt: string;
}

export interface CoachIndividualBookingResponse {
  bookingId: string;
  bookingType: IndividualBookingType;
  sessionId: string | null;
  scheduleId: string | null;
  coachMemberId: string | null;
  coachName: string;
  locationName: string;
  startsAt: string | null;
  dayOfWeek: string | null;
  startTime: string | null;
  duration: number;
  courtBookingMode: CourtBookingMode;
  booking: IndividualBookingSummary;
  pricePerSession: number;
}

export interface OnSiteBillResponse {
  billId: string;
  studentMemberId: string;
  studentName: string;
  description: string;
  amount: number;
  remainingAmount: number;
  status: 'Pending' | 'PartiallyPaid' | 'Paid' | 'Cancelled';
  createdAt: string;
  sessionStartsAt: string | null;
  coachName: string;
}
